<template>
  <AppWithLoading v-slot:default="{ loader }">
    <VCard class="dialog">
      <VCardTitle :class="textClass" class="dialog-header justify-start mb-1 flex-column align-start">
        <img
          :src="require('@/assets/img/tarrif-change-warning.svg')"
          alt="warning"
          class="dialog-header__icon"
        >
        <VBtn
          class="v-btn__close"
          icon
          small
          absolute
          top
          right
          @click="$emit('close')"
        >
          <IconClose />
        </VBtn>
        <div class="dialog-header__text">
          <template v-if="title">
            {{ title }}
          </template>
          <template v-else>
            {{ maxVacanciesTitle }}
          </template>
        </div>
      </VCardTitle>
      <VCardText class="dialog-text">
        <template v-if="text">
          {{ text }}
        </template>
        <template v-else>
          {{ maxVacanciesText }}
        </template>
      </VCardText>
      <slot name="btn">
        <VBtn
          class="v-btn__send mb-0"
          color="success"
          id="inviteBtn"
          @click="loader(() => redirectToUpgradePlan())"
        >
          {{ maxVacanciesTextButton }}
        </VBtn>
      </slot>
    </VCard>
  </AppWithLoading>
</template>

<script>
import { paymentTabs, settingsTabs } from '@/enums';
import AppWithLoading from '@/components/AppWithLoading';
import { computed, toRefs } from '@vue/composition-api';
import { useI18n } from 'vue-i18n-composable';
import useAccount from '@/compositions/accounting/useAccount';
import IconClose from './SvgIcons/Close.vue';

export default {
  name: 'DialogLimitedVacancies',
  components: { AppWithLoading, IconClose },
  props: {
    title: {
      type: String,
      default: '',
      required: false,
    },
    text: {
      type: String,
      default: '',
      required: false,
    },
    textClass: {
      type: String,
      default: 'dark--text',
    },
    planTitle: {
      type: String,
      required: true,
    },
    limitedVacancies: {
      type: Number,
      required: false,
      default: 0,
    },
  },
  setup(props, { root: { $router } }) {
    const { t, tc } = useI18n();
    const { planTitle, limitedVacancies } = toRefs(props);
    const { account } = useAccount();

    const isTrialMaximumVacancies = computed(() => account.value?.trialMaximumVacancies && account.value.trialEnabled);

    const maxVacanciesTitle = computed(() => {
      return tc('maxVacanciesTitle', limitedVacancies.value, { count: limitedVacancies.value });
    });
    const maxVacanciesText = computed(() => {
      if (isTrialMaximumVacancies.value) {
        return t('maxVacanciesTrialText1', { tariff: planTitle.value })
          + tc('maxVacanciesTrialText2', limitedVacancies.value, { count: limitedVacancies.value })
          + t('maxVacanciesTrialText3');
      }

      return t('maxVacanciesText1', { tariff: planTitle.value })
        + tc('maxVacanciesText2', limitedVacancies.value, { count: limitedVacancies.value })
        + t('maxVacanciesText3');
    });

    const maxVacanciesTextButton = computed(() => {
      return isTrialMaximumVacancies.value ? t('toUpgradePlanTrial') : t('toUpgradePlan');
    });

    async function redirectToUpgradePlan() {
      await $router.push({
        name: 'settings',
        params: { type: settingsTabs.PAYMENT, addType: paymentTabs.PLANS },
      });
    }

    return {
      maxVacanciesTitle,
      maxVacanciesText,
      redirectToUpgradePlan,
      maxVacanciesTextButton,
    };
  },
};
</script>

<style scoped lang="scss">
.v-btn {
  &__send {
    width: 100%;
    font-weight: 400;
    font-size: 16px;
    line-height: 19.2px;
    margin-bottom: 30px;
  }
  &__close {
    top: 15px;
    right: 15px;
  }
}

.dialog {
  padding: 30px;
  border-radius: 8px !important;
  color: #212121 !important;
}

.dialog-header {
  font-weight: 400;
  font-size: 24px;
  line-height: 26px;
  padding: 0;
  &__icon {
    padding: 13px;
    background: rgba(234, 13, 13, 0.1);
    border-radius: 50%;
  }
  &__text {
    text-align: start;
    margin: 20px 0;
  }
}
.dialog-text {
  padding: 0;
  margin-bottom: 40px;
  font-size: 14px;
  line-height: 19.6px;
  font-weight: 300;
}
</style>

<i18n locale="en" lang="yaml">
  maxVacanciesTitle: "Only {count} job are available
  | Only {count} jobs are available
  | Only {count} jobs are available"
  maxVacanciesText1: 'You are currently on the "{tariff}" plan, which '
  maxVacanciesText2: "allows for only {count} active job.
  | allows for only {count} active jobs.
  | allows for only {count} active jobs."
  maxVacanciesText3: ' To post more jobs, you need to upgrade your plan.'
  toUpgradePlan: Upgrade plan
  toUpgradePlanTrial: Upgrade to a pricing plan
  maxVacanciesTrialText1: 'You have an active trial period in which '
  maxVacanciesTrialText2: "allows for only {count} active job.
  | allows for only {count} active jobs.
  | allows for only {count} active jobs."
  maxVacanciesTrialText3: ' To accommodate more vacancies, you need to switch to a paid tariff'
</i18n>

<i18n locale="ru" lang="yaml">
  maxVacanciesTitle: "Доступно только {count} вакансий
  | Доступна только {count} вакансия
  | Доступно только {count} вакансии
  | Доступно только {count} вакансий"
  maxVacanciesText1: 'У вас активен тарифный план «{tariff}», на котором '
  maxVacanciesText2: "доступно только {count} активных вакансий.
  | доступна только {count} активная вакансия.
  | доступно только {count} активные вакансии.
  | доступно только {count} активных вакансий."
  maxVacanciesText3: ' Для размещения большего количества вакансий необходимо повысить тарифный план.'
  toUpgradePlan: Повысить тариф
  toUpgradePlanTrial: Перейти на тариф
  maxVacanciesTrialText1: 'У вас активен пробный период, на котором '
  maxVacanciesTrialText2: "доступно только {count} активных вакансий.
  | доступна только {count} активная вакансия.
  | доступно только {count} активные вакансии.
  | доступно только {count} активных вакансий."
  maxVacanciesTrialText3: ' Для размещения большего количества вакансий необходимо перейти на платный тариф'
</i18n>
